/**
 * Composição de canteiro de obras: mobilização (01.91.00), desmobilização
 * (01.91.01) e administração local (01.99.01), no formato do "Relatório de
 * composições" EMBASA - cada composição é uma lista de serviços (C) e insumos
 * (I) com coeficiente, cotação e total.
 *
 * - Mobilização e desmobilização: os coeficientes saem da memória de cálculo
 *   da planilha "MEMORIAL DE CALCULO MOB, DESMOB - REFORMA FILTROS ETA
 *   PRINCIPAL.xlsx" (referências de célula nos comentários), que dimensiona o
 *   canteiro pelo número máximo de operários simultâneos. Códigos e ordem das
 *   linhas seguem "COMPOSICOES MOB, DESMOB E ADM LOCAL-REFORMA FILTROS ETA PR.xlsx".
 * - Administração local: insumos da tabela de insumos EMBASA, quantidade × multiplicador;
 *   a mão de obra (classe M.O.) recebe as leis sociais de mensalista da tabela
 *   do orçamento (cotação = preço do insumo × (1 + L.S.)).
 *
 * Código puro (sem banco): usado na tela, na API e nos documentos gerados.
 */
import type { TipoComposicao } from "./tipos";

export interface Dimensao {
  quantidade: number;
  largura: number;
  comprimento: number;
}

/**
 * Linha digitada pelo orçamentista: equipe/despesas da administração local e
 * itens avulsos de mobilização/desmobilização. Com código, descrição, unidade e
 * cotação vêm da tabela (serviço ou insumo); sem código é uma linha livre.
 */
export interface LinhaAvulsa {
  id: string;
  /** Vazio = linha livre (sem código EMBASA). */
  codigo: string;
  /** Vazio = descrição oficial do código. */
  descricao: string;
  /** Vazio = unidade do código. */
  unidade: string;
  quantidade: number;
  /** Coeficiente = quantidade × multiplicador; null = multiplicador padrão (adm. local) ou 1. */
  multiplicador: number | null;
  /** null = cotação da tabela pelo código. */
  cotacao: number | null;
}

export type LinhaAdministracao = LinhaAvulsa;

/**
 * Item acrescentado pelo orçamentista na memória de cálculo ("7 - Outros"):
 * resultado = quantidade × comprimento × largura × altura (fator vazio = não entra).
 * Se tiver composição de destino, vira uma linha dela.
 */
export interface ItemOutros {
  id: string;
  descricao: string;
  unidade: string;
  quantidade: number;
  comprimento: number | null;
  largura: number | null;
  altura: number | null;
  /** null = só documenta a memória, não gera linha. */
  composicao: TipoAvulso | null;
  /** Vazio = linha sem código (usa a cotação digitada). */
  codigo: string;
  /** null = cotação da tabela pelo código. */
  cotacao: number | null;
}

export type TipoAvulso = Exclude<TipoComposicao, "administracao_local">;

export interface ParametrosCanteiro {
  operarios: number;
  cerca_altura: number;
  passeio_largura: number;
  brita_altura: number;
  estacionamento: { leves: Dimensao; grandes: Dimensao; caminhoes: Dimensao; faixa_quantidade: number; faixa_largura: number };
  /** Área mínima (m²) por conjunto/pessoa - NR-18. */
  areas_minimas: { lavatorio: number; vaso: number; mictorio: number; chuveiro: number; vestiario: number; refeitorio: number };
  operarios_por_conjunto: number;
  operarios_por_chuveiro: number;
  centrais: { forma: boolean; aco: boolean };
  espessuras_entulho: { piso: number; cobertura: number; tapume: number };
  dmt_km: number;
  grama_percentual: number;
  m2_por_arvore: number;
  /** Código EMBASA escolhido para cada linha (chave → código); ausente = padrão. */
  codigos: Record<string, string>;
  /** Coeficiente digitado à mão (chave → valor); ausente = memória de cálculo. */
  coeficientes: Record<string, number>;
  /** Linhas que o orçamentista retirou da composição. */
  excluidas: string[];
  administracao: { multiplicador_padrao: number; linhas: LinhaAvulsa[] };
  /** Itens avulsos acrescentados às composições de mobilização e desmobilização. */
  avulsos: Record<TipoAvulso, LinhaAvulsa[]>;
  /** Seção "7 - Outros" da memória de cálculo. */
  outros: ItemOutros[];
}

export const COMPOSICOES_CANTEIRO: Record<TipoComposicao, { codigo: string; descricao: string }> = {
  mobilizacao: { codigo: "01.91.00/EMBASA", descricao: "MOBILIZACAO E INSTALACAO DE CANTEIRO DE OBRAS" },
  desmobilizacao: { codigo: "01.91.01/EMBASA", descricao: "DESMOBILIZACAO DE CANTEIRO DE OBRAS" },
  administracao_local: { codigo: "01.99.01/EMBASA", descricao: "ADMINISTRACAO LOCAL" },
};

const dim = (quantidade: number, largura: number, comprimento: number): Dimensao => ({ quantidade, largura, comprimento });

const adm = (id: string, codigo: string, quantidade: number, multiplicador: number | null = null): LinhaAvulsa => ({
  id,
  codigo,
  descricao: "",
  unidade: "",
  quantidade,
  multiplicador,
  cotacao: null,
});

export function parametrosPadrao(): ParametrosCanteiro {
  return {
    operarios: 35,
    cerca_altura: 2.2,
    passeio_largura: 1.2,
    brita_altura: 0.03,
    estacionamento: { leves: dim(1, 2.2, 4.7), grandes: dim(1, 2.5, 5.5), caminhoes: dim(1, 3.5, 12), faixa_quantidade: 1, faixa_largura: 7 },
    areas_minimas: { lavatorio: 0.6, vaso: 1, mictorio: 0.6, chuveiro: 0.8, vestiario: 1.5, refeitorio: 1 },
    operarios_por_conjunto: 20,
    operarios_por_chuveiro: 10,
    centrais: { forma: false, aco: false },
    espessuras_entulho: { piso: 0.07, cobertura: 0.05, tapume: 0.05 },
    dmt_km: 20,
    grama_percentual: 0.8,
    m2_por_arvore: 200,
    codigos: {},
    coeficientes: {},
    excluidas: [],
    administracao: {
      multiplicador_padrao: 0,
      // Mesma equipe/despesas das composições de referência (quantidade 0 = não entra).
      linhas: [
        adm("almoxarife", "B570002049/EMBASA", 1),
        adm("seguranca", "B570002004/EMBASA", 1),
        adm("engenheiro", "B550000104/EMBASA", 1),
        adm("tecnico", "B570002034/EMBASA", 1),
        adm("encarregado", "B570002055/EMBASA", 1),
        adm("zelador", "B570003040/EMBASA", 1),
        adm("agua", "F099800029/EMBASA", 0),
        adm("energia", "F099800030/EMBASA", 0),
        adm("telefone", "F099800031/EMBASA", 1),
        adm("limpeza", "F029500005/EMBASA", 1),
        adm("escritorio", "F029500008/EMBASA", 1),
        adm("armarios", "F020000713/EMBASA", 1, 1),
        adm("cadeiras", "F020000707/EMBASA", 6, 1),
        adm("mesas", "F020000704/EMBASA", 2, 1),
        adm("computador", "F099800061/EMBASA", 1),
        adm("bebedouro", "E209300235/EMBASA", 1),
      ],
    },
    avulsos: { mobilizacao: [], desmobilizacao: [] },
    outros: [],
  };
}

// ------------------------------------------------------------------ normalização
function numero(valor: unknown, padrao: number): number {
  if (valor === undefined || valor === null || valor === "") return padrao;
  const n = typeof valor === "number" ? valor : Number(String(valor).trim().replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : padrao;
}

function objeto(valor: unknown): Record<string, unknown> {
  return valor && typeof valor === "object" && !Array.isArray(valor) ? (valor as Record<string, unknown>) : {};
}

function numeros<T extends object>(entrada: unknown, padrao: T): T {
  const o = objeto(entrada);
  return Object.fromEntries(Object.entries(padrao).map(([k, v]) => [k, numero(o[k], v as number)])) as T;
}

/** Aceita o JSON salvo/enviado (parcial ou de versão anterior) e completa com os padrões. */
export function normalizarParametros(entrada: unknown): ParametrosCanteiro {
  const p = parametrosPadrao();
  const e = objeto(entrada);
  const est = objeto(e.estacionamento);
  const centrais = objeto(e.centrais);
  const admin = objeto(e.administracao);

  const opcional = (v: unknown, padrao: number) => (v === null || v === undefined || v === "" ? null : numero(v, padrao));
  const linhasAvulsas = (lista: unknown, prefixo: string): LinhaAvulsa[] =>
    (Array.isArray(lista) ? lista : []).map((l, i) => {
      const o = objeto(l);
      return {
        id: String(o.id ?? `${prefixo}-${i}`),
        codigo: String(o.codigo ?? "").trim().toUpperCase(),
        descricao: String(o.descricao ?? "").trim(),
        unidade: String(o.unidade ?? "").trim(),
        quantidade: numero(o.quantidade, 0),
        multiplicador: opcional(o.multiplicador, 1),
        cotacao: opcional(o.cotacao, 0),
      };
    });
  const linhas = Array.isArray(admin.linhas) ? linhasAvulsas(admin.linhas, "linha") : p.administracao.linhas;
  const avulsos = objeto(e.avulsos);

  return {
    operarios: Math.round(numero(e.operarios, p.operarios)),
    cerca_altura: numero(e.cerca_altura, p.cerca_altura),
    passeio_largura: numero(e.passeio_largura, p.passeio_largura),
    brita_altura: numero(e.brita_altura, p.brita_altura),
    estacionamento: {
      leves: numeros(est.leves, p.estacionamento.leves),
      grandes: numeros(est.grandes, p.estacionamento.grandes),
      caminhoes: numeros(est.caminhoes, p.estacionamento.caminhoes),
      faixa_quantidade: numero(est.faixa_quantidade, p.estacionamento.faixa_quantidade),
      faixa_largura: numero(est.faixa_largura, p.estacionamento.faixa_largura),
    },
    areas_minimas: numeros(e.areas_minimas, p.areas_minimas),
    operarios_por_conjunto: Math.max(1, numero(e.operarios_por_conjunto, p.operarios_por_conjunto)),
    operarios_por_chuveiro: Math.max(1, numero(e.operarios_por_chuveiro, p.operarios_por_chuveiro)),
    centrais: { forma: Boolean(centrais.forma), aco: Boolean(centrais.aco) },
    espessuras_entulho: numeros(e.espessuras_entulho, p.espessuras_entulho),
    dmt_km: numero(e.dmt_km, p.dmt_km),
    grama_percentual: Math.min(numero(e.grama_percentual, p.grama_percentual), 1),
    m2_por_arvore: numero(e.m2_por_arvore, p.m2_por_arvore),
    codigos: Object.fromEntries(
      Object.entries(objeto(e.codigos))
        .map(([k, v]) => [k, String(v ?? "").trim().toUpperCase()])
        .filter(([, v]) => v),
    ),
    coeficientes: Object.fromEntries(
      Object.entries(objeto(e.coeficientes))
        .map(([k, v]) => [k, numero(v, -1)] as const)
        .filter(([, v]) => v >= 0),
    ),
    excluidas: Array.isArray(e.excluidas) ? e.excluidas.map(String) : [],
    administracao: { multiplicador_padrao: numero(admin.multiplicador_padrao, p.administracao.multiplicador_padrao), linhas },
    avulsos: {
      mobilizacao: linhasAvulsas(avulsos.mobilizacao, "mob"),
      desmobilizacao: linhasAvulsas(avulsos.desmobilizacao, "desmob"),
    },
    outros: (Array.isArray(e.outros) ? e.outros : []).map((item, i): ItemOutros => {
      const o = objeto(item);
      return {
        id: String(o.id ?? `outro-${i}`),
        descricao: String(o.descricao ?? "").trim(),
        unidade: String(o.unidade ?? "").trim(),
        quantidade: numero(o.quantidade, 0),
        comprimento: opcional(o.comprimento, 0),
        largura: opcional(o.largura, 0),
        altura: opcional(o.altura, 0),
        composicao: o.composicao === "mobilizacao" || o.composicao === "desmobilizacao" ? o.composicao : null,
        codigo: String(o.codigo ?? "").trim().toUpperCase(),
        cotacao: opcional(o.cotacao, 0),
      };
    }),
  };
}

// ------------------------------------------------------------------ cálculo
/** ROUND(x; 0) do Excel (meio para longe do zero). */
const arred = (x: number) => Math.sign(x) * Math.round(Math.abs(x));
/** ROUNDUP(x; 0) do Excel para valores positivos (tolerância para ruído de ponto flutuante). */
const arredCima = (x: number) => Math.ceil(x - 1e-9);

/** Faixa das tabelas por número de operários: até 30 | 31 a 100 | acima de 100. */
function faixa<T>(operarios: number, ate30: T, ate100: T, acima: T): T {
  return operarios < 31 ? ate30 : operarios < 101 ? ate100 : acima;
}

export const TABELAS_CANTEIRO = {
  ambulatorio: { ate49: 0, de50a100: 2.2 * 5, acima100: 2.2 * 5 * 2 },
  comodos: {
    contratada: [3 * 4, 4 * 4, 4 * 7],
    almoxarifado: [3 * 4, 4 * 8, 4 * 10],
    deposito_cimento: [2 * 4, 4 * 4, 4 * 7.5],
  },
  centrais: { forma: [10 * 4, 10 * 5, 10 * 8], aco: [12 * 3, 12 * 4, 12 * 6] },
  silos: { areia: [1, 2, 3], solo: [1, 1, 2], brita: [1, 1, 2] },
} as const;

export interface MemorialCanteiro {
  operarios: number;
  terreno: { largura: number; comprimento: number; area: number };
  cerca: { altura: number; extensao: number; area: number };
  area_ocupada: number;
  passeio: { largura: number; extensao: number; area: number };
  brita: { area_terreno: number; area_ocupada: number; altura: number; volume: number };
  estacionamento: { leves: number; grandes: number; caminhoes: number; faixa_comprimento: number; faixa: number; total: number };
  sanitarios: {
    conjuntos: number;
    chuveiros: number;
    lavatorio: number;
    vaso: number;
    mictorio: number;
    chuveiro: number;
    area_sanitarios: number;
    vestiario: number;
    refeitorio: number;
  };
  ambulatorio: number;
  comodos: { contratada: number; almoxarifado: number; deposito_cimento: number; total: number };
  centrais: { forma: number; aco: number };
  silos: { areia: number; solo: number; brita: number; total: number; caixa_dagua: number };
  desmobilizacao: {
    demolicao: { instalacoes: number; ambulatorio: number; passeio: number; comodos: number; centrais: number; total: number };
    retirada: { instalacoes: number; ambulatorio: number; tapume: number; comodos: number; centrais: number; total: number };
    entulho: { piso: number; cobertura: number; tapume: number; total: number };
    transporte: { volume: number; dmt: number; momento: number };
    regularizacao: number;
    grama: number;
    arvores: number;
  };
}

export function calcularCanteiro(p: ParametrosCanteiro): MemorialCanteiro {
  const op = p.operarios;
  const fx = faixa(op, 0, 1, 2);

  // 2 - Instalações sanitárias / local de refeições (C35:H39)
  const conjuntos = arredCima(op / p.operarios_por_conjunto);
  const chuveiros = arredCima(op / p.operarios_por_chuveiro);
  const lavatorio = p.areas_minimas.lavatorio * conjuntos;
  const vaso = p.areas_minimas.vaso * conjuntos;
  const mictorio = p.areas_minimas.mictorio * conjuntos;
  const chuveiro = p.areas_minimas.chuveiro * chuveiros;
  const areaSanitarios = lavatorio + vaso + mictorio + chuveiro; // C39
  const vestiario = p.areas_minimas.vestiario * op; // G39
  const refeitorio = p.areas_minimas.refeitorio * op; // H39

  // 3 - Ambulatório (C49): frentes com 50 ou mais trabalhadores
  const t = TABELAS_CANTEIRO;
  const ambulatorio = op < 50 ? t.ambulatorio.ate49 : op < 101 ? t.ambulatorio.de50a100 : t.ambulatorio.acima100;

  // 4 - Escritórios / cômodos (D57:F57)
  const contratada = t.comodos.contratada[fx];
  const almoxarifado = t.comodos.almoxarifado[fx];
  const depositoCimento = t.comodos.deposito_cimento[fx];
  const comodos = contratada + almoxarifado + depositoCimento;

  // 5 - Centrais (C65:D65; na planilha de exemplo não foram consideradas)
  const forma = p.centrais.forma ? t.centrais.forma[fx] : 0;
  const aco = p.centrais.aco ? t.centrais.aco[fx] : 0;
  const centrais = forma + aco;

  // 5 - Silos para agregados (C73:F74)
  const areia = t.silos.areia[fx];
  const solo = t.silos.solo[fx];
  const britaSilo = t.silos.brita[fx];
  const silos = areia + solo + britaSilo; // C74
  const caixaDagua = op < 101 ? arred((op * 70) / 1000) : 0; // F73

  // Passeio (C18:C20)
  const edificacoes = areaSanitarios + vestiario + refeitorio + ambulatorio + comodos + centrais;
  const passeioExtensao = arred(edificacoes / 5); // C19
  const passeioArea = p.passeio_largura * passeioExtensao; // C20

  // 6 - Desmobilização: demolição de piso/cobertura e retirada de tapume (C79:G84)
  const instalacoes = areaSanitarios + vestiario + refeitorio;
  const demolicaoTotal = instalacoes + ambulatorio + passeioArea + comodos + centrais; // C84
  const areaOcupada = demolicaoTotal; // C15

  // Estacionamento (C24:F28)
  const e = p.estacionamento;
  const area = (d: Dimensao) => d.quantidade * d.largura * d.comprimento;
  const faixaComprimento = arredCima((e.leves.quantidade * e.leves.largura + e.grandes.quantidade * e.grandes.largura + e.caminhoes.quantidade * e.caminhoes.largura) / 2);
  const faixaArea = e.faixa_quantidade * e.faixa_largura * faixaComprimento;
  const estacionamento = area(e.leves) + area(e.grandes) + area(e.caminhoes) + faixaArea; // F28

  // Dimensões do terreno (G4:G5)
  const largura = Math.max(arred(Math.sqrt(areaOcupada + estacionamento + silos * 8) / 5) * 5 - 5, 7);
  const comprimento = Math.max(arredCima((areaOcupada + estacionamento + silos * 4) / largura / 5) * 5, 15);
  const areaTerreno = largura * comprimento; // C12

  // Cerca / muro (G10:G12)
  const cercaExtensao = (largura + comprimento) * 2;
  const cercaArea = p.cerca_altura * cercaExtensao;

  // Brita (G18:G21)
  const britaVolume = (areaTerreno - areaOcupada) * p.brita_altura;

  const retiradaTotal = instalacoes + ambulatorio + cercaArea + comodos + centrais; // G84

  // Carga manual de entulho (C87:G90)
  const entPiso = demolicaoTotal * p.espessuras_entulho.piso;
  const entCobertura = demolicaoTotal * p.espessuras_entulho.cobertura;
  const entTapume = retiradaTotal * p.espessuras_entulho.tapume;
  const entulho = entPiso + entCobertura + entTapume;

  // Regularização, grama e árvores (G93:G100)
  const regularizacao = largura * comprimento;

  return {
    operarios: op,
    terreno: { largura, comprimento, area: areaTerreno },
    cerca: { altura: p.cerca_altura, extensao: cercaExtensao, area: cercaArea },
    area_ocupada: areaOcupada,
    passeio: { largura: p.passeio_largura, extensao: passeioExtensao, area: passeioArea },
    brita: { area_terreno: areaTerreno, area_ocupada: areaOcupada, altura: p.brita_altura, volume: britaVolume },
    estacionamento: {
      leves: area(e.leves),
      grandes: area(e.grandes),
      caminhoes: area(e.caminhoes),
      faixa_comprimento: faixaComprimento,
      faixa: faixaArea,
      total: estacionamento,
    },
    sanitarios: { conjuntos, chuveiros, lavatorio, vaso, mictorio, chuveiro, area_sanitarios: areaSanitarios, vestiario, refeitorio },
    ambulatorio,
    comodos: { contratada, almoxarifado, deposito_cimento: depositoCimento, total: comodos },
    centrais: { forma, aco },
    silos: { areia, solo, brita: britaSilo, total: silos, caixa_dagua: caixaDagua },
    desmobilizacao: {
      demolicao: { instalacoes, ambulatorio, passeio: passeioArea, comodos, centrais, total: demolicaoTotal },
      retirada: { instalacoes, ambulatorio, tapume: cercaArea, comodos, centrais, total: retiradaTotal },
      entulho: { piso: entPiso, cobertura: entCobertura, tapume: entTapume, total: entulho },
      transporte: { volume: entulho, dmt: p.dmt_km, momento: entulho * p.dmt_km },
      regularizacao,
      grama: regularizacao * p.grama_percentual,
      arvores: p.m2_por_arvore > 0 ? arred(regularizacao / p.m2_por_arvore) : 0,
    },
  };
}


// ------------------------------------------------------------------ composição
export interface ServicoCanteiro {
  chave: string;
  tipo: Exclude<TipoComposicao, "administracao_local">;
  /** Rótulo da memória de cálculo (a descrição oficial vem da tabela). */
  rotulo: string;
  codigo_padrao: string;
  /** Unidade da memória de cálculo (o código EMBASA escolhido deve ter a mesma). */
  unidade: string;
  coeficiente: (m: MemorialCanteiro) => number;
}

/** Linhas na ordem das composições 01.91.00 e 01.91.01 de referência. */
export const SERVICOS_CANTEIRO: ServicoCanteiro[] = [
  { chave: "passeio", tipo: "mobilizacao", rotulo: "Passeio", codigo_padrao: "18.01.05/EMBASA", unidade: "M2", coeficiente: (m) => m.passeio.area },
  { chave: "brita", tipo: "mobilizacao", rotulo: "Brita no terreno livre", codigo_padrao: "18.02.07/EMBASA", unidade: "M3", coeficiente: (m) => m.brita.volume },
  { chave: "limpeza", tipo: "mobilizacao", rotulo: "Limpeza do terreno", codigo_padrao: "55.01.01/EMBASA", unidade: "M2", coeficiente: (m) => m.terreno.area },
  { chave: "regularizacao_mob", tipo: "mobilizacao", rotulo: "Regularização do terreno", codigo_padrao: "55.01.04/EMBASA", unidade: "M2", coeficiente: (m) => m.terreno.area },
  { chave: "escritorio", tipo: "mobilizacao", rotulo: "Escritório da contratada", codigo_padrao: "55.04.02/EMBASA", unidade: "M2", coeficiente: (m) => m.comodos.contratada },
  { chave: "almoxarifado", tipo: "mobilizacao", rotulo: "Almoxarifado / escritório", codigo_padrao: "55.04.04/EMBASA", unidade: "M2", coeficiente: (m) => m.comodos.almoxarifado },
  { chave: "sanitarios", tipo: "mobilizacao", rotulo: "Sanitários", codigo_padrao: "55.04.11/EMBASA", unidade: "M2", coeficiente: (m) => m.sanitarios.area_sanitarios },
  { chave: "vestiario", tipo: "mobilizacao", rotulo: "Vestiário", codigo_padrao: "55.04.13/EMBASA", unidade: "M2", coeficiente: (m) => m.sanitarios.vestiario },
  { chave: "refeitorio", tipo: "mobilizacao", rotulo: "Refeitório", codigo_padrao: "55.04.15/EMBASA", unidade: "M2", coeficiente: (m) => m.sanitarios.refeitorio },
  { chave: "ambulatorio", tipo: "mobilizacao", rotulo: "Ambulatório", codigo_padrao: "55.04.08/EMBASA", unidade: "M2", coeficiente: (m) => m.ambulatorio },
  { chave: "central_forma", tipo: "mobilizacao", rotulo: "Central de forma", codigo_padrao: "55.04.17/EMBASA", unidade: "M2", coeficiente: (m) => m.centrais.forma },
  { chave: "central_aco", tipo: "mobilizacao", rotulo: "Central de armação", codigo_padrao: "55.04.18/EMBASA", unidade: "M2", coeficiente: (m) => m.centrais.aco },
  { chave: "deposito_cimento", tipo: "mobilizacao", rotulo: "Depósito de cimento", codigo_padrao: "55.04.19/EMBASA", unidade: "M2", coeficiente: (m) => m.comodos.deposito_cimento },
  { chave: "silos", tipo: "mobilizacao", rotulo: "Silos (areia, solo e brita)", codigo_padrao: "55.04.31/EMBASA", unidade: "UN", coeficiente: (m) => m.silos.total },
  { chave: "caixa_dagua", tipo: "mobilizacao", rotulo: "Caixa d'água 1.000 L", codigo_padrao: "16.92.00/EMBASA", unidade: "UN", coeficiente: (m) => m.silos.caixa_dagua },
  { chave: "tapume", tipo: "mobilizacao", rotulo: "Cerca / tapume", codigo_padrao: "55.05.02/EMBASA", unidade: "M2", coeficiente: (m) => m.cerca.area },
  { chave: "demolicao_piso", tipo: "desmobilizacao", rotulo: "Demolição de piso", codigo_padrao: "02.01.25/EMBASA", unidade: "M2", coeficiente: (m) => m.desmobilizacao.demolicao.total },
  { chave: "demolicao_cobertura", tipo: "desmobilizacao", rotulo: "Demolição de cobertura", codigo_padrao: "02.01.40/EMBASA", unidade: "M2", coeficiente: (m) => m.desmobilizacao.demolicao.total },
  { chave: "retirada_tapume", tipo: "desmobilizacao", rotulo: "Retirada de tapume e barracões", codigo_padrao: "02.01.50/EMBASA", unidade: "M2", coeficiente: (m) => m.desmobilizacao.retirada.total },
  { chave: "carga_entulho", tipo: "desmobilizacao", rotulo: "Carga manual de entulho", codigo_padrao: "06.01.19/EMBASA", unidade: "M3", coeficiente: (m) => m.desmobilizacao.entulho.total },
  { chave: "transporte_entulho", tipo: "desmobilizacao", rotulo: "Transporte de entulho (momento)", codigo_padrao: "06.02.07/EMBASA", unidade: "M3XKM", coeficiente: (m) => m.desmobilizacao.transporte.momento },
  { chave: "grama", tipo: "desmobilizacao", rotulo: "Plantio de grama", codigo_padrao: "18.06.01/EMBASA", unidade: "M2", coeficiente: (m) => m.desmobilizacao.grama },
  { chave: "arvores", tipo: "desmobilizacao", rotulo: "Plantio de árvores", codigo_padrao: "18.06.07/EMBASA", unidade: "UN", coeficiente: (m) => m.desmobilizacao.arvores },
  { chave: "regularizacao", tipo: "desmobilizacao", rotulo: "Regularização do terreno", codigo_padrao: "55.01.04/EMBASA", unidade: "M2", coeficiente: (m) => m.desmobilizacao.regularizacao },
];

/** Cotação de um código no contexto do orçamento: serviço da tabela de preços (C) ou insumo (I). */
export interface PrecoCodigo {
  codigo: string;
  descricao: string;
  unidade: string | null;
  tipo: "C" | "I";
  /** Classe do insumo (M.O., MAT., SER.MO...) ou do serviço. */
  classe: string | null;
  /** Preço da tabela, antes das leis sociais. */
  preco_base: number;
  /** Leis sociais aplicadas (%) - só mão de obra de insumo. */
  leis_sociais: number | null;
  preco_unitario: number;
}

export interface LinhaCanteiro {
  chave: string;
  tipo: TipoComposicao;
  codigo: string;
  /** Descrição oficial (ou o rótulo, se o código não foi encontrado). */
  descricao: string;
  encontrado: boolean;
  tipo_codigo: "C" | "I";
  unidade: string | null;
  coeficiente: number;
  /** Coeficiente pela memória de cálculo (antes de ajuste manual). */
  coeficiente_calculado: number;
  cotacao: number;
  total: number;
  incluida: boolean;
  /** De onde veio o coeficiente (texto curto para a tela e os documentos). */
  memoria: string;
}

const fmt = (x: number) => x.toLocaleString("pt-BR", { maximumFractionDigits: 3 });

const CODIGO_SERVICO = /^\d{2}\.\d{2}\.\d{2}(\/|$)/;

/** Resultado de um item de "7 - Outros": produto dos fatores preenchidos. */
export function valorOutro(item: ItemOutros): number {
  return item.quantidade * (item.comprimento ?? 1) * (item.largura ?? 1) * (item.altura ?? 1);
}

export function formulaOutro(item: ItemOutros): string {
  const fatores = [item.quantidade, item.comprimento, item.largura, item.altura].filter((f): f is number => f !== null);
  return fatores.map(fmt).join(" × ");
}

/** "C" = serviço da tabela de preços (NN.NN.NN), "I" = insumo. */
export function tipoDoCodigo(codigo: string | null | undefined): "C" | "I" {
  return CODIGO_SERVICO.test(codigo ?? "") ? "C" : "I";
}

export function codigosUsados(p: ParametrosCanteiro): string[] {
  const codigos = SERVICOS_CANTEIRO.map((s) => p.codigos[s.chave] || s.codigo_padrao);
  for (const l of [...p.administracao.linhas, ...p.avulsos.mobilizacao, ...p.avulsos.desmobilizacao, ...p.outros]) {
    if (l.codigo) codigos.push(l.codigo);
  }
  return [...new Set(codigos)];
}

/** Linhas das três composições (inclusive retiradas/zeradas, marcadas como não incluídas). */
export function linhasCanteiro(p: ParametrosCanteiro, precos: Record<string, PrecoCodigo | undefined>): LinhaCanteiro[] {
  const m = calcularCanteiro(p);
  const linhas: LinhaCanteiro[] = [];

  const linhaAvulsa = (tipo: TipoComposicao, chave: string, l: LinhaAvulsa, padrao: number): LinhaCanteiro => {
    const preco = l.codigo ? precos[l.codigo] : undefined;
    const multiplicador = l.multiplicador ?? padrao;
    const coeficiente = l.quantidade * multiplicador;
    const cotacao = l.cotacao ?? preco?.preco_unitario ?? 0;
    const ls = preco?.leis_sociais;
    const origemCotacao =
      l.cotacao !== null
        ? " · cotação digitada"
        : ls !== null && ls !== undefined && preco
          ? ` · cotação = ${fmt(preco.preco_base)} × (1 + L.S. ${fmt(ls)}%)`
          : "";
    return {
      chave,
      tipo,
      codigo: l.codigo,
      descricao: l.descricao || preco?.descricao || l.codigo || "Item avulso",
      encontrado: Boolean(preco) || l.cotacao !== null,
      tipo_codigo: preco?.tipo ?? tipoDoCodigo(l.codigo),
      unidade: l.unidade || preco?.unidade || null,
      coeficiente,
      coeficiente_calculado: coeficiente,
      cotacao,
      total: coeficiente * cotacao,
      incluida: coeficiente > 0,
      memoria: `${fmt(l.quantidade)} × ${fmt(multiplicador)}${origemCotacao}`,
    };
  };

  for (const tipo of ["mobilizacao", "desmobilizacao"] as const) {
    for (const s of SERVICOS_CANTEIRO.filter((x) => x.tipo === tipo)) {
      const codigo = p.codigos[s.chave] || s.codigo_padrao;
      const preco = precos[codigo];
      const calculado = s.coeficiente(m);
      const manual = p.coeficientes[s.chave];
      const coeficiente = manual ?? calculado;
      const cotacao = preco?.preco_unitario ?? 0;
      linhas.push({
        chave: s.chave,
        tipo: s.tipo,
        codigo,
        descricao: preco?.descricao ?? s.rotulo,
        encontrado: Boolean(preco),
        tipo_codigo: preco?.tipo ?? tipoDoCodigo(codigo),
        unidade: preco?.unidade ?? s.unidade,
        coeficiente,
        coeficiente_calculado: calculado,
        cotacao,
        total: coeficiente * cotacao,
        incluida: coeficiente > 0 && !p.excluidas.includes(s.chave),
        memoria: manual === undefined ? `${s.rotulo}: memória de cálculo` : `${s.rotulo}: digitado (memória ${fmt(calculado)})`,
      });
    }
    for (const item of p.outros.filter((o) => o.composicao === tipo)) {
      const chave = `outro:${item.id}`;
      const preco = item.codigo ? precos[item.codigo] : undefined;
      const calculado = valorOutro(item);
      const manual = p.coeficientes[chave];
      const coeficiente = manual ?? calculado;
      const cotacao = item.cotacao ?? preco?.preco_unitario ?? 0;
      const rotulo = `7 - Outros: ${item.descricao || "item"}`;
      linhas.push({
        chave,
        tipo,
        codigo: item.codigo,
        descricao: preco?.descricao ?? (item.descricao || "Outros"),
        encontrado: Boolean(preco) || item.cotacao !== null,
        tipo_codigo: preco?.tipo ?? tipoDoCodigo(item.codigo),
        unidade: preco?.unidade ?? (item.unidade || null),
        coeficiente,
        coeficiente_calculado: calculado,
        cotacao,
        total: coeficiente * cotacao,
        incluida: coeficiente > 0 && !p.excluidas.includes(chave),
        memoria:
          (manual === undefined ? `${rotulo} (${formulaOutro(item)})` : `${rotulo}: digitado (memória ${fmt(calculado)})`) +
          (item.cotacao !== null ? " · cotação digitada" : ""),
      });
    }
    for (const l of p.avulsos[tipo]) linhas.push(linhaAvulsa(tipo, `avulso:${tipo}:${l.id}`, l, 1));
  }

  for (const l of p.administracao.linhas) {
    linhas.push(linhaAvulsa("administracao_local", `adm:${l.id}`, l, p.administracao.multiplicador_padrao));
  }
  return linhas;
}

export interface RespostaCanteiro {
  parametros: ParametrosCanteiro;
  salvo: boolean;
  precos: Record<string, PrecoCodigo>;
  /** Leis sociais de mensalista da tabela do orçamento (%), aplicadas à mão de obra da administração local. */
  leis_sociais_mensalista: number | null;
}

// ------------------------------------------------------------------ memória para documentos
export interface SecaoMemoria {
  titulo: string;
  linhas: { rotulo: string; valor: number; unidade: string }[];
}

/** Memória de cálculo em seções (mesma ordem da planilha de referência), para PDF e Excel. */
export function secoesMemoria(p: ParametrosCanteiro): SecaoMemoria[] {
  const m = calcularCanteiro(p);
  const d = m.desmobilizacao;
  const l = (rotulo: string, valor: number, unidade: string) => ({ rotulo, valor, unidade });
  return [
    {
      titulo: "Dados gerais",
      linhas: [
        l("Nº máximo de operários simultâneos", m.operarios, "un"),
        l("Largura do terreno", m.terreno.largura, "m"),
        l("Comprimento do terreno", m.terreno.comprimento, "m"),
      ],
    },
    {
      titulo: "1 - Preparação do terreno",
      linhas: [
        l("Limpeza do terreno / regularização (largura × comprimento)", m.terreno.area, "m²"),
        l("Cerca/muro - altura", m.cerca.altura, "m"),
        l("Cerca/muro - extensão total", m.cerca.extensao, "m"),
        l("Cerca/muro - área", m.cerca.area, "m²"),
        l("Locação - área ocupada", m.area_ocupada, "m²"),
        l("Passeio - largura", m.passeio.largura, "m"),
        l("Passeio - extensão total", m.passeio.extensao, "m"),
        l("Passeio - área", m.passeio.area, "m²"),
        l("Brita - (área do terreno − área ocupada) × altura", m.brita.volume, "m³"),
        l("Estacionamento - veículos leves", m.estacionamento.leves, "m²"),
        l("Estacionamento - veículos grandes", m.estacionamento.grandes, "m²"),
        l("Estacionamento - caminhões / retro", m.estacionamento.caminhoes, "m²"),
        l(`Estacionamento - faixa de acesso (comprimento ${fmt(m.estacionamento.faixa_comprimento)} m)`, m.estacionamento.faixa, "m²"),
        l("Estacionamento - área considerada", m.estacionamento.total, "m²"),
      ],
    },
    {
      titulo: "2 - Instalações sanitárias / local de refeições",
      linhas: [
        l(`Conjuntos lavatório/vaso/mictório (1 a cada ${fmt(p.operarios_por_conjunto)} operários)`, m.sanitarios.conjuntos, "un"),
        l(`Chuveiros (1 a cada ${fmt(p.operarios_por_chuveiro)} operários)`, m.sanitarios.chuveiros, "un"),
        l("Sanitários - área considerada", m.sanitarios.area_sanitarios, "m²"),
        l("Vestiário - área considerada", m.sanitarios.vestiario, "m²"),
        l("Refeitório - área considerada", m.sanitarios.refeitorio, "m²"),
      ],
    },
    { titulo: "3 - Ambulatório (frentes com 50 ou mais trabalhadores)", linhas: [l("Área considerada", m.ambulatorio, "m²")] },
    {
      titulo: "4 - Escritórios / cômodos",
      linhas: [
        l("Contratada", m.comodos.contratada, "m²"),
        l("Almoxarifado / escritório", m.comodos.almoxarifado, "m²"),
        l("Depósito de cimento", m.comodos.deposito_cimento, "m²"),
      ],
    },
    {
      titulo: "5 - Centrais e silos",
      linhas: [
        l("Central de forma", m.centrais.forma, "m²"),
        l("Central de aço", m.centrais.aco, "m²"),
        l("Silos - areia", m.silos.areia, "un"),
        l("Silos - solo", m.silos.solo, "un"),
        l("Silos - brita", m.silos.brita, "un"),
        l("Caixa d'água (70 L por operário, 1.000 L cada)", m.silos.caixa_dagua, "un"),
      ],
    },
    {
      titulo: "6 - Desmobilização",
      linhas: [
        l("Demolição piso/cobertura - total (instalações, ambulatório, passeio, cômodos, centrais)", d.demolicao.total, "m²"),
        l("Retirada de tapume - total (instalações, ambulatório, tapume, cômodos, centrais)", d.retirada.total, "m²"),
        l(`Entulho - demolição de piso (e = ${fmt(p.espessuras_entulho.piso)} m)`, d.entulho.piso, "m³"),
        l(`Entulho - demolição de cobertura (e = ${fmt(p.espessuras_entulho.cobertura)} m)`, d.entulho.cobertura, "m³"),
        l(`Entulho - retirada de tapume (e = ${fmt(p.espessuras_entulho.tapume)} m)`, d.entulho.tapume, "m³"),
        l("Carga manual de entulho - total", d.entulho.total, "m³"),
        l(`Transporte de entulho (DMT ${fmt(d.transporte.dmt)} km)`, d.transporte.momento, "m³·km"),
        l("Regularização do terreno", d.regularizacao, "m²"),
        l(`Plantio de grama (${fmt(p.grama_percentual * 100)}% do terreno)`, d.grama, "m²"),
        l(`Plantio de árvores (1 a cada ${fmt(p.m2_por_arvore)} m²)`, d.arvores, "un"),
      ],
    },
    ...(p.outros.length
      ? [
          {
            titulo: "7 - Outros",
            linhas: p.outros.map((o) => l(`${o.descricao || "Item"} (${formulaOutro(o)})`, valorOutro(o), o.unidade)),
          },
        ]
      : []),
  ];
}
